/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import { Header } from '@blend/enterprise-components';
import React from 'react';
import { enterpriseColors } from '../../style/colors';
import { SearchResults } from '@components/TypeaheadSearch/types';

const defaultMaxHeight = 600;

/** This component is an adapted version of the same component from blend-bridge repo */
const styles = StyleSheet.create({
  wrapper: {
    left: 0,
    right: 0,
    position: 'absolute',
    backgroundColor: enterpriseColors.WHITE,
    zIndex: 1000,
    overflowY: 'auto',
    boxShadow: '0px 1px 4px 0px rgba(20, 56, 89, 0.15)',
  },
  dropDown: {
    border: `1px solid ${enterpriseColors.CLOUD_50}`,
    backgroundColor: enterpriseColors.WHITE,
  },
  totalCount: {
    padding: '8px 16px',
    color: enterpriseColors.NEUTRAL_500,
    fontSize: 11,
  },
  result: {
    ':focus': {
      backgroundColor: enterpriseColors.NEUTRAL_25,
    },
    ':hover': {
      backgroundColor: enterpriseColors.NEUTRAL_25,
    },
    cursor: 'pointer',
    display: 'flex',
  },
  resetUL: {
    marginBlockStart: 0,
    marginBlockEnd: 0,
    paddingInlineStart: 0,
  },
});

export namespace TypeaheadResults {
  export interface Props<T> {
    name: string;
    ResultComponent: React.FC<{ result: T; highlight: string }>;
    isInputFocused: boolean;
    maxResultsHeight?: number;
    onResultClick?: (result: T) => void;
    highlight: string;
    results: SearchResults<T>;
    resultLimit?: number;
  }
}

/** TypeaheadResults renders list of results for TypeaheadSearch component */
export const TypeaheadResults = <T,>({
  name,
  isInputFocused,
  maxResultsHeight,
  onResultClick,
  ResultComponent,
  results,
  highlight,
  resultLimit = 5,
}: TypeaheadResults.Props<T>) => {
  if (!isInputFocused) {
    return null;
  }

  return (
    <div className={css(styles.wrapper)} data-it={`${name}-search-results`}>
      <div className={css(styles.dropDown)}>
        <ul className={css(styles.resetUL)} style={{ maxHeight: maxResultsHeight || defaultMaxHeight }}>
          {results.items.slice(0, resultLimit).map((result, index: number) => (
            <li
              className={`${css(styles.result)}`}
              data-it={`typeahead-result-${index}`}
              key={`${name}-result-${index}`}
              onClick={() => onResultClick && onResultClick(result)}
              tabIndex={0}
            >
              <ResultComponent result={result} highlight={highlight} />
            </li>
          ))}
        </ul>
        <div data-it={`${name}-totalCount-wrapper`} className={css(styles.totalCount)}>
          <Header size={6}>
            <span data-it={`${name}-totalCount-number`}>{results.totalCount}</span>{' '}
            <span>{`${results.totalCount === 1 ? 'RESULT' : 'RESULTS'} FOUND`}</span>
          </Header>
        </div>
      </div>
    </div>
  );
};
